import { useAuth0 } from '@auth0/auth0-react';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAppSelector } from 'src/hooks/store';
import { selectedWarehouseIdSelector } from 'src/state/selectors/delivery';
import { getUserRoles } from 'src/utils/authRoles';
import { buildLaunchDarklyContext } from './launchDarklyContext';
import { MockLaunchDarklyContext } from './launchDarklyService';

type IdentityStatus = 'loading' | 'ready' | 'unavailable';
const IdentityContext = createContext<IdentityStatus>('unavailable');

export const useLaunchDarklyIdentity = (): IdentityStatus =>
  useContext(IdentityContext);

// Identify from Auth0, including direct-login callbacks that never visit /login.
// Consumers gating user access must wait rather than use anonymous/previous-user flags.
export const LaunchDarklyIdentity = ({
  children,
}: {
  children: JSX.Element;
}): JSX.Element => {
  const { isAuthenticated, isLoading, user } = useAuth0();
  const ldClient = useLDClient();
  const isMocked = useContext(MockLaunchDarklyContext);
  const warehouseId = useAppSelector(selectedWarehouseIdSelector);
  const email =
    isAuthenticated && !process.env.CYPRESS_AUTH_MOCKED
      ? user?.email
      : undefined;
  // getUserRoles returns a new array on every render. Compare the contents so
  // re-renders and token refreshes don't identify again.
  const rolesJson = JSON.stringify(getUserRoles(user));
  const request = useMemo(
    () => ({
      client: ldClient,
      context: email
        ? buildLaunchDarklyContext({
            email,
            roles: JSON.parse(rolesJson) as string[],
            warehouseId,
          })
        : undefined,
    }),
    [ldClient, email, rolesJson, warehouseId],
  );
  const pending = useRef<Promise<unknown>>(Promise.resolve());
  const [completed, setCompleted] = useState<{
    request: typeof request;
    status: 'ready' | 'unavailable';
  }>();

  useEffect(() => {
    if (!request.client || !request.context) return;
    let cancelled = false;

    const { client, context } = request;
    // The SDK applies each identify response when it arrives. Serialize changes
    // so a slower previous-user response cannot overwrite the current flags.
    pending.current = pending.current
      .then(() => {
        if (cancelled) return undefined;
        return client.identify(context);
      })
      .then(
        () => {
          if (!cancelled) setCompleted({ request, status: 'ready' });
        },
        () => {
          if (!cancelled) setCompleted({ request, status: 'unavailable' });
        },
      );

    return () => {
      cancelled = true;
    };
  }, [request]);

  let status: IdentityStatus = 'unavailable';
  if (isLoading) {
    status = 'loading';
  } else if (isMocked && email) {
    status = 'ready';
  } else if (request.client && request.context) {
    status = completed?.request === request ? completed.status : 'loading';
  }

  return (
    <IdentityContext.Provider value={status}>
      {children}
    </IdentityContext.Provider>
  );
};
